import { Grid } from "@mui/material";
import * as React from "react";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as riskService from "../../../models/services/riskService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode } from "../../../models/unit/unitModuleNode";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { UnitModuleSettings } from "../../unit/Unit/UnitModuleSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";
import { RiskTemplateGrid } from "./RiskTemplateGrid";
import { SaveDataObj } from "source/framework/saveData";
import { useEffect, useRef } from "react";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { RiskClassificationNodesTree, RiskClassificationType } from "./RiskClassificationNodesTree/RiskClassificationNodesTree";

/* eslint-disable no-unused-vars */
export const enum RiskSettingsDialogMenuItem {
    Units = "1",
    Permissions = "2",
    RiskCategories = "3",
    RiskProcesses = "4",
    RiskStandards = "5",
    RiskStakeholders = "6",
    //CompanyRiskTemplates = "7",
    DefaultRiskCategories = "8",
    DefaultRiskProcesses = "9",
    DefaultRiskStandards = "10",
    DefaultRiskStakeholders = "11",
    DefaultRiskTemplates = "12"
}
/* eslint-enable no-unused-vars */

interface IRiskSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    settingsActions: IPageNavMenuItem[];
    onClose: () => void;
}

interface IRiskSettingsDialogState {
    // Settings
    settings: IRiskSettingEdit;
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;
    //showRiskTemplatesByUnitsDialog: boolean;
    activeMenuItem: RiskSettingsDialogMenuItem;
}

export const RiskSettingsDialog = (props: IRiskSettingsDialogProp) => {
    const unitsRef = useRef<UnitModuleSettings>(null);
    const permissionsRef = useRef<UnitPermissionSettingsNew>(null);
    const [state, setState] = React.useState<IRiskSettingsDialogState>({
        settings: null,
        unitModuleNode: null,
        unitNodePermissions: null,
        //showRiskTemplatesByUnitsDialog: false,
        activeMenuItem: props.settingsActions.length > 0
            ? props.settingsActions[0].id as RiskSettingsDialogMenuItem
            : RiskSettingsDialogMenuItem.RiskCategories,
    });

    useEffect(() => {
        const loadData = async () => {
            await loadSettings();
            if (state.activeMenuItem === RiskSettingsDialogMenuItem.Units) {
                await loadUnits();
            } else if (state.activeMenuItem === RiskSettingsDialogMenuItem.Permissions) {
                await loadPermissions();
            }
        };

        loadData();
    }, []);

    // #region Settings
    const loadSettings = async () => {
        const settings = await AppUtils.callService(() => riskService.getRiskSettings(props.companyId), false);
        if (!settings) return;
        setState((old) => ({
            ...old,
            settings
        }));
    };

    const handleRiskSettingsSave = async (data: { riskProcessInUse?: boolean, riskStandardInUse?: boolean, riskStakeholderInUse?: boolean, riskProcessName?: string }) => {
        const saveData = new SaveDataObj();
        saveData.append("companyId", props.companyId);
        saveData.append("riskProcessInUse", data.riskProcessInUse ?? state.settings.riskProcessInUse);
        saveData.append("riskStandardInUse", data.riskStandardInUse ?? state.settings.riskStandardInUse);
        saveData.append("riskStakeholderInUse", data.riskStakeholderInUse ?? state.settings.riskStakeholderInUse);
        saveData.append("riskProcessName", data.riskProcessName ?? state.settings.riskProcessName);
        const result = await riskService.saveRiskSettings(saveData.obj);
        if (!result) return;
        setState((old) => ({
            ...old,
            settings: {
                ...old.settings,
                ...data
            }
        }));
    };
    // #endregion Settings

    // #region Units
    const onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await unitsRef.current.handleCancel(false);
    };

    const loadUnits = async () => {
        const result = await AppUtils.callService(() => riskService.getUnits(props.companyId), false);
        if (!result) return;
        setState((old) => ({
            ...old,
            unitModuleNode: result.unitModuleNode
        }));
    };

    const handleUnitModuleSettingsSave = async (obj: any) => {
        return await riskService.saveUnits(obj);
    };
    // #endregion Units

    // #region Permissions
    const onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await permissionsRef.current.handleCancel(false);
    };

    const loadPermissions = async () => {
        const result = await AppUtils.callService(() => riskService.getUnitNodePermissions(props.companyId), false);
        if (!result) return;
        setState((old) => ({
            ...old,
            unitNodePermissions: result
        }));
    };

    const handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await riskService.saveUnitNodePermissions(obj);
        loadPermissions();
        return result;
    };
    // #endregion Permissions

    const handleSubViewOk = (_closeDialog: boolean) => {
    };

    const handleToggleActiveMenuItem = async (menuItem: RiskSettingsDialogMenuItem) => {
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === RiskSettingsDialogMenuItem.Units) {
            if (!await onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === RiskSettingsDialogMenuItem.Permissions) {
            if (!await onUnitPermissionSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === RiskSettingsDialogMenuItem.Units) {
            await loadUnits();
        } else if (menuItem === RiskSettingsDialogMenuItem.Permissions) {
            await loadPermissions();
        }
        //Set new tab active
        setState((old) => ({
            ...old,
            activeMenuItem: menuItem
        }));
    };

    const handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        props.onClose();
    };

    return (
        <>
            <Dialog
                id="riskSettingsDialog"
                title={Translations.Settings}
                maxWidth="lg"
                maxHeight="100%"
                onClose={() => handleCancel(true)}
                body={
                    <GridRow flexGrow={2}>
                        <Grid item sm={3}>
                            <PageNavMenuItems
                                activeId={state.activeMenuItem}
                                items={props.settingsActions}
                                onToggleMenuItem={handleToggleActiveMenuItem}
                            />
                        </Grid>
                        <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.Units) && (
                                <UnitModuleSettings
                                    ref={unitsRef}
                                    infoText={Translations.RiskModuleInfoUnits}
                                    companyId={props.companyId}
                                    sessionState={props.sessionState}
                                    applicationPermissionType={ApplicationPermissionType.Risk}
                                    unitModuleNode={state.unitModuleNode}
                                    onCancel={handleCancel}
                                    onOk={handleSubViewOk}
                                    onSave={handleUnitModuleSettingsSave}
                                />
                            )}
                            {state.activeMenuItem === RiskSettingsDialogMenuItem.Permissions && (
                                <UnitPermissionSettingsNew
                                    ref={permissionsRef}
                                    infoText={Translations.RiskModuleInfoPermissions}
                                    companyId={props.companyId}
                                    unitNodePermissions={state.unitNodePermissions}
                                    cardView={true}
                                    onSave={handleUnitPermissionSettingsSave}
                                    onOk={handleSubViewOk}
                                    onCancel={handleCancel}
                                />
                            )}
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.RiskCategories || state.activeMenuItem === RiskSettingsDialogMenuItem.DefaultRiskCategories) &&
                                <RiskClassificationNodesTree
                                    infoText={Translations.RiskModuleInfoRiskCategories}
                                    riskClassificationType={RiskClassificationType.RiskCategory}
                                    companyId={state.activeMenuItem === RiskSettingsDialogMenuItem.RiskCategories ? props.companyId : null}
                                />
                            }
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.RiskProcesses || state.activeMenuItem === RiskSettingsDialogMenuItem.DefaultRiskProcesses) &&
                                <RiskClassificationNodesTree
                                    infoText={Translations.RiskModuleInfoRiskProcesses}
                                    riskClassificationType={RiskClassificationType.RiskProcess}
                                    titleTitle={Translations.RiskProcessName}
                                    title={state.settings?.riskProcessName ?? ""}
                                    inUseTitle={Translations.RiskProcessesInUse}
                                    inUse={state.settings?.riskProcessInUse ?? false}
                                    companyId={state.activeMenuItem === RiskSettingsDialogMenuItem.RiskProcesses ? props.companyId : null}
                                    onSaveInUseAndTitle={(inUse: boolean, name: string) => handleRiskSettingsSave({ riskProcessInUse: inUse, riskProcessName: name })}
                                />
                            }
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.RiskStandards || state.activeMenuItem === RiskSettingsDialogMenuItem.DefaultRiskStandards) &&
                                <RiskClassificationNodesTree
                                    infoText={Translations.RiskModuleInfoRiskStandards}
                                    riskClassificationType={RiskClassificationType.RiskStandard}
                                    inUseTitle={Translations.RiskStandardsInUse}
                                    inUse={state.settings?.riskStandardInUse ?? false}
                                    companyId={state.activeMenuItem === RiskSettingsDialogMenuItem.RiskStandards ? props.companyId : null}
                                    onSaveInUseAndTitle={(inUse: boolean, _name: string) => handleRiskSettingsSave({ riskStandardInUse: inUse })}
                                />
                            }
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.RiskStakeholders || state.activeMenuItem === RiskSettingsDialogMenuItem.DefaultRiskStakeholders) &&
                                <RiskClassificationNodesTree
                                    infoText={Translations.RiskModuleInfoRiskStakeholders}
                                    riskClassificationType={RiskClassificationType.RiskStakeholder}
                                    inUseTitle={Translations.RiskStakeholdersInUse}
                                    inUse={state.settings?.riskStakeholderInUse ?? false}
                                    companyId={state.activeMenuItem === RiskSettingsDialogMenuItem.RiskStakeholders ? props.companyId : null}
                                    onSaveInUseAndTitle={(inUse: boolean, _name: string) => handleRiskSettingsSave({ riskStakeholderInUse: inUse })}
                                />
                            }
                            {(state.activeMenuItem === RiskSettingsDialogMenuItem.DefaultRiskTemplates) &&
                                <RiskTemplateGrid
                                    companyId={null}
                                    sessionState={props.sessionState}
                                />
                            }
                        </Grid>
                    </GridRow>
                }
                buttons={
                    state.activeMenuItem !== RiskSettingsDialogMenuItem.Units && state.activeMenuItem !== RiskSettingsDialogMenuItem.Permissions
                        ? [{ title: Translations.Close, variant: "outlined", onClick: () => handleCancel(true) }]
                        : null
                }
            />
        </>
    );
};
